import {
  ArrowsClockwiseIcon,
  CheckCircleIcon,
  GiftIcon,
  TagIcon,
  UsersThreeIcon,
} from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { useState } from "react";

import { useAccountCurrency } from "@/client/services/currency/currency";

import { DashboardContent } from "../../components/layout/dashboard-content";
import { AmbassadorUsagesList } from "./components/ambassador-usages-list";
import { PromoCodeCard } from "./components/promo-code-card";
import { useAmbassadorDashboard } from "./hooks/use-ambassador-dashboard";
import type { AmbassadorStatusFilter } from "./types/ambassador";

type MetricCardProps = {
  label: string;
  value: string | number;
  tone: "indigo" | "emerald" | "amber";
  icon: React.ReactNode;
};

const MetricCard = ({ label, value, tone, icon }: MetricCardProps) => {
  const tones = {
    indigo: "bg-indigo-50 text-indigo-700",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
  };

  return (
    <article className="flex min-h-[84px] min-w-0 items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <span className={cn("grid size-10 shrink-0 place-items-center rounded-xl", tones[tone])}>
        {icon}
      </span>

      <div className="min-w-0">
        <p className="text-[11px] font-medium text-slate-500 sm:text-xs">{label}</p>

        <p
          className={cn(
            "mt-1 truncate text-lg font-bold sm:text-xl",
            tone === "emerald" ? "text-emerald-600" : "text-indigo-950",
          )}
        >
          {value}
        </p>
      </div>
    </article>
  );
};

const LoadingState = () => {
  return (
    <div role="status" className="space-y-3 motion-safe:animate-pulse">
      <span className="sr-only">Chargement de l’espace ambassadeur…</span>

      <div className="h-28 rounded-2xl bg-slate-200/70" />

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="h-24 rounded-2xl bg-slate-200/70" />
        <div className="h-24 rounded-2xl bg-slate-200/70" />
        <div className="h-24 rounded-2xl bg-slate-200/70" />
      </div>

      <div className="h-64 rounded-2xl bg-slate-200/70" />
    </div>
  );
};

export const AmbassadorPage = () => {
  const { format } = useAccountCurrency();

  const [page, setPage] = useState(1);

  const [filter, setFilter] = useState<AmbassadorStatusFilter>("all");

  const { data, usages, loading, refreshing, error, refetch, createCode, creatingCode } =
    useAmbassadorDashboard(page);

  const filteredUsages =
    filter === "all" ? usages : usages.filter((usage) => usage.status === filter);

  const activeCount = data?.confirmedSales ?? 0;
  const inactiveCount = (data?.pagination?.total ?? 0) - activeCount;

  const handleFilterChange = (nextFilter: AmbassadorStatusFilter) => {
    setFilter(nextFilter);
    setPage(1);
  };

  return (
    <DashboardContent>
      <section className="min-w-0 space-y-4">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-indigo-950 sm:text-2xl">
              Ambassadeur
            </h1>

            <p className="mt-1 max-w-2xl text-xs leading-5 text-slate-600 sm:text-[13px]">
              Partagez votre code promo. Vos proches profitent d’une réduction et vous recevez une
              commission après validation de leur achat.
            </p>
          </div>

          <button
            type="button"
            disabled={refreshing}
            onClick={() => void refetch()}
            className={cn(
              "flex min-h-10 shrink-0 items-center justify-center gap-2",
              "rounded-xl border border-slate-200 bg-white px-4",
              "text-xs font-semibold text-indigo-700 shadow-sm",
              "transition-colors hover:bg-indigo-50",
              "focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-indigo-500 disabled:opacity-50",
            )}
          >
            <ArrowsClockwiseIcon
              aria-hidden
              size={16}
              className={refreshing ? "animate-spin" : undefined}
            />

            {refreshing ? "Actualisation…" : "Actualiser"}
          </button>
        </header>

        {loading ? (
          <LoadingState />
        ) : !data ? (
          <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
            <p className="text-sm font-semibold text-rose-800">
              Impossible de charger votre espace ambassadeur
            </p>

            <p className="mt-1 text-xs leading-5 text-rose-700">
              Vérifiez que le backend est démarré et que votre session est toujours valide.
            </p>

            <button
              type="button"
              onClick={() => void refetch()}
              className={cn(
                "mt-3 min-h-10 rounded-xl",
                "border border-rose-300 px-4",
                "text-xs font-semibold text-rose-800",
                "transition-colors hover:bg-rose-100",
                "focus-visible:outline-none focus-visible:ring-2",
                "focus-visible:ring-rose-500",
              )}
            >
              Réessayer
            </button>
          </div>
        ) : (
          <>
            {error && (
              <div
                role="alert"
                className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-900"
              >
                L’actualisation a échoué. Les dernières données disponibles restent affichées.
              </div>
            )}

            {!data.eligible && (
              <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4">
                <p className="text-sm font-semibold text-indigo-950">
                  Programme ambassadeur verrouillé
                </p>

                <p className="mt-1 text-xs leading-5 text-indigo-800">
                  Vous devez d’abord acheter au moins une offre SMART payante pour obtenir votre
                  code ambassadeur.
                </p>
              </div>
            )}

            <PromoCodeCard isEligible={data.eligible} promoCode={data.code ?? null} />

            {data.eligible && !data.code && (
              <button
                type="button"
                disabled={creatingCode}
                onClick={() => void createCode()}
                className={cn(
                  "flex min-h-11 w-full items-center justify-center gap-2",
                  "rounded-xl bg-indigo-700 px-5",
                  "text-xs font-semibold text-white",
                  "transition-colors hover:bg-indigo-800",
                  "focus-visible:outline-none focus-visible:ring-2",
                  "focus-visible:ring-indigo-500 disabled:cursor-not-allowed",
                  "disabled:opacity-50 sm:w-auto",
                )}
              >
                <TagIcon aria-hidden size={18} weight="duotone" />

                {creatingCode ? "Génération du code…" : "Générer mon code ambassadeur"}
              </button>
            )}

            <div className="grid gap-3 sm:grid-cols-3">
              <MetricCard
                label="Utilisations du code"
                value={data.sales.length}
                tone="indigo"
                icon={<UsersThreeIcon aria-hidden size={22} weight="duotone" />}
              />

              <MetricCard
                label="Achats confirmés"
                value={data.confirmedSales}
                tone="emerald"
                icon={<CheckCircleIcon aria-hidden size={22} weight="duotone" />}
              />

              <MetricCard
                label="Commissions gagnées"
                value={format(data.earnedFcfa)}
                tone="amber"
                icon={<GiftIcon aria-hidden size={22} weight="duotone" />}
              />
            </div>

            <AmbassadorUsagesList
              usages={filteredUsages}
              activeCount={activeCount}
              inactiveCount={inactiveCount}
              filter={filter}
              page={page}
              total={data.pagination.total}
              pageSize={data.pagination.pageSize}
              onFilterChange={handleFilterChange}
              onPageChange={setPage}
            />
          </>
        )}
      </section>
    </DashboardContent>
  );
};
